import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { translate } from '@optimusgps/optimus-intl';
import { message, Typography } from 'antd';
import axios, { CancelToken } from 'axios';
import { Branding } from '@/features/branding/branding.dto';
import BrandingService from '@/features/branding/branding.service';
import { amazonImageUrl } from '@/utilities/util';

export type BrandLogoImageProps = {
	className?: string;
	style?: CSSProperties;
};

const DEFAULT_BRAND_LOGO_IMAGE_PROPS: BrandLogoImageProps = {
	style: { width: '100px', height: '100px' },
};

export const CONTACT_SUPPORT_BRAND_LOGO_IMAGE_PROPS: BrandLogoImageProps = {
	className: 'imageLogo',
	style: { paddingTop: '10px' },
};

export const STRIPE_PUBLIC_CARD_BRAND_LOGO_IMAGE_PROPS: BrandLogoImageProps = {
	className: 'stripe-activation-page-logo',
};

const renderBrandLogo = (
	brand: Branding | undefined | null,
	logoImageProps?: BrandLogoImageProps
): ReactNode | null => {
	if (!brand) {
		return null;
	}

	if (brand.hasImage) {
		const imageUrl = amazonImageUrl({ folder: 'logos', name: `branding_${brand.id}` });
		const resolved = logoImageProps ?? DEFAULT_BRAND_LOGO_IMAGE_PROPS;
		return <img src={imageUrl} alt="Logo" className={resolved.className} style={resolved.style} />;
	}

	return <Typography.Title style={{ textAlign: 'center' }}>{brand.name}</Typography.Title>;
};

export function useBranding(logoImageProps?: BrandLogoImageProps): {
	brand: Branding | undefined;
	logo: ReactNode | null;
} {
	const intl = useIntl();
	const [brand, setBrand] = useState<Branding | undefined>();
	const [logo, setLogo] = useState<ReactNode | null>(null);

	const fetchBranding = async (token: CancelToken) => {
		try {
			const { success: brandResult, error } = await BrandingService.getBrandForLogin(token);
			if (error) {
				message.error({
					content: intl.formatMessage(translate('commonError')),
					key: 'commonError',
				});
				return;
			}
			setBrand(brandResult);
			setLogo(renderBrandLogo(brandResult, logoImageProps));
		} catch {
			message.error({
				content: intl.formatMessage(translate('commonError')),
				key: 'commonError',
			});
		}
	};

	useEffect(() => {
		const source = axios.CancelToken.source();
		fetchBranding(source.token);
		return () => {
			source.cancel();
		};
	}, [logoImageProps]);

	return { brand, logo };
}
